// TODO: split authData into user, project, session (or whatever makes sense)

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { vueAuthData } from '@/api/vue';
import { setNewTokenFn } from '@/api/csrf';
import { useAppAlerts } from '@/stores/app/alerts';
import { TYPE_TAPWAGE } from '@/helpers/constants/integrationTypes';
import { DEMOGRAPHIC_SURVEY_SHOW, DEMOGRAPHIC_SURVEY_RESTRICTED } from '@/helpers/constants/demographicSurveyVersions';

export const useAppAuthData = defineStore('appAuthData', () => {
  const appAlerts = useAppAlerts();

  // True after a logged-in auth-data load. Guest fetches on /login do not set this,
  // so the first navigation after sign-in still blocks until the session is loaded.
  const hasFetchedAuthData = ref(false);

  // If two parts of the app ask for auth-data at once, only one request runs.
  let authDataRequestInFlight = null;

  const authData = ref({
    appMaintenanceMode: false,
    atsName: '',
    atsId: '',
    hrisName: '',
    hrisId: null,
    integrationTypeKey: 0,
    integrationType: '',
    isSuperuser: false,
    isLoggedIn: false,
    userType: null,
    userSetupStatus: null,
    companyName: null,
    projectName: null,
    projectId: null,
    projectStatus: null,
    projectSetupStatus: null,
    projectMaintenanceMode: false,
    projectImageUrl: null,
    extensionJobsEnabled: false,
    showIntegrationsPage: false,
    isTrackingEnabled: false,
    loginMethod: '',
    oauthClient: '',
    ssoName: '',
    canDemographicSurveyReport: false,
    demographicSurveyReportVersion: 0,
    aiEnabled: false,
    noEditorEnabled: false,
    isRoleManagementRestricted: false,
    jobProfileEnabled: false,
    flsaEnabled: false,
    acknowledgementsEnabled: false,
    nextUiEnabled: false,
    ascentEnabled: false,
    inclusionMeterDisabled: false,
    accountExecutiveEmail: '',
    accountExecutiveName: '',
    userId: null,
    showWeeklyReports: false,
    isApplicationReportingEnabled: false,
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    appVersion: '',
    personaKey: null,
    hasInstalledDA: false,
    ipAddress: null,
    jobProfileLevels: [],
    rbac: {
      roleNames: [],
      permissions: {},
    },
  });

  const integrated = computed(() => authData.value.integrationTypeKey !== TYPE_TAPWAGE);
  const integrationName = computed(() => (integrated.value ? authData.value.integrationType : ''));
  const isDatapeopleEmployee = computed(() => [
    'datapeople.io',
    'datapeople.fun',
    'taprecruit.me',
    'taprecruit.co',
    'tapwage.com',
    'payscale.com',
  ].some((domain) => authData.value.email?.endsWith(domain)) || authData.value.isSuperuser);
  const isDemographicSurveyReportVisible = computed(() => [
    DEMOGRAPHIC_SURVEY_SHOW,
    DEMOGRAPHIC_SURVEY_RESTRICTED,
  ].includes(authData.value.demographicSurveyReportVersion));
  const isDemographicSurveyReportRestricted = computed(
    () => authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_RESTRICTED,
  );
  const demographicSurveyReportLabel = computed(() => {
    if (authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_SHOW) {
      return 'Show';
    }
    if (authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_RESTRICTED) {
      return 'Restricted';
    }
    return 'Hide';
  });
  const isAdmin = computed(() => authData.value.role === 'admin');

  const setAuthData = (data) => {
    const validData = {};

    // Only write data that we are expecting
    Object.keys(data).forEach((key) => {
      if (authData.value[key] !== undefined) {
        validData[key] = data[key];
      }
    });

    authData.value = validData;
  };

  const updatePersona = (personaKey) => {
    authData.value.personaKey = personaKey;
  };

  const addFlashMessages = (flashMessages) => {
    Object.keys(flashMessages || {}).forEach((type) => {
      flashMessages[type].forEach((message) => {
        appAlerts.addAlert({ type, message });
      });
    });
  };

  const reportAuthDataFetchError = (e, isBackgroundRefresh) => {
    if (
      e.message.endsWith('not found in appAuthData state')
      && ['app.datapeople.io', 'sandbox.datapeople.io'].includes(window.location.origin)
    ) {
      // eslint-disable-next-line no-console
      console.error(e.message);
      return;
    }

    if (isBackgroundRefresh) {
      // Background refresh failed. Keep showing the data we already have.
      // eslint-disable-next-line no-console
      console.error(e);
      return;
    }

    window.alert(`AuthData: ${e}`);
    throw e;
  };

  /* Fetching authData initially waits before loading the page,
  then updates in the background when you change pages. */
  const fetchAuthData = async ({ forNavigation = false } = {}) => {
    if (!authDataRequestInFlight) {
      authDataRequestInFlight = (async () => {
        try {
          const { flashMessages, ...responseJSON } = await vueAuthData();

          setAuthData(responseJSON);
          addFlashMessages(flashMessages);

          hasFetchedAuthData.value = responseJSON.isLoggedIn === true;
          setNewTokenFn(() => fetchAuthData());
        } finally {
          authDataRequestInFlight = null;
        }
      })();
    }

    if (forNavigation && hasFetchedAuthData.value) {
      authDataRequestInFlight.catch((error) => {
        reportAuthDataFetchError(error, true);
      });
      return;
    }

    try {
      await authDataRequestInFlight;
    } catch (error) {
      reportAuthDataFetchError(error, false);
    }
  };

  const toggleNextUi = () => {
    authData.value.nextUiEnabled = !authData.value.nextUiEnabled;
  };

  const setAiEnabled = (enabled) => {
    authData.value.aiEnabled = !!enabled;
  };

  const checkRbacPermissions = (permissions) => permissions.every(
    (permission) => Boolean(authData.value.rbac?.permissions?.[permission]),
  );

  const checkOneOfPermissions = (permissions = []) => permissions
    .some((permission) => authData.value.rbac?.permissions?.[permission]);

  return {
    authData,
    integrated,
    integrationName,
    isAdmin,
    fetchAuthData,
    setAuthData,
    toggleNextUi,
    setAiEnabled,
    updatePersona,
    checkRbacPermissions,
    checkOneOfPermissions,
    isDatapeopleEmployee,
    isDemographicSurveyReportVisible,
    isDemographicSurveyReportRestricted,
    demographicSurveyReportLabel,
  };
});
